import React, { useCallback, useEffect, useLayoutEffect, useMemo, useState, } from 'react';
import { View, Keyboard, } from 'react-native';
import getSymbolFromCurrency from 'currency-symbol-map';
import Button from 'design-system/Button';
import StatusBar from 'design-system/StatusBar';
import Text, { styles } from 'design-system/Text';
import { AppView } from 'design-system/AppView';
import Spacer from 'design-system/Spacer';
import { rem } from 'design-system/values';
import { AnimatedKeyboardAwareScrollView } from 'design-system/AnimatedComponents';
import BlurAmountView from 'design-system/BlurAmountView';
import Alert from 'utils/packages/Alert';
import { selectIsScrambled, useTextInput } from 'hooks';
import Amplitude from 'utils/amplitude';
import { formatAmount } from 'utils/formatting';
import createStyleSheets from 'utils/createStyleSheets';
import { MoneyTextInput } from 'utils/packages/moneyInput';
import { useMoneyInputText } from 'hooks/useMoneyInputText';
import withAnimatedNavigationHeader from 'design-system/hoc/withAnimatedNavigationHeader';
import useAppRating from 'hooks/useAppRating';
import { useAppDispatch, useAppSelector, useAppStore } from 'store/hooks';
import { selectIsLoggedIn, selectDefaultCurrency } from 'reducers/selectors';
import HelpButton from 'login/components/HelpButton';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import Amount from 'design-system/Amount';
import { useAutoFocusInput } from 'hooks/useAutoFocusInput';
import useStyles from 'hooks/useStyles';
import { useRequiredBenefitCallbackWithSpaces } from 'features/premium/hooks/useRequiredBenefitCallback';
import { fetchAnalyticsBudgetingData, getIncomes } from 'actions';
import useColors from 'hooks/useColors';
import InfoCard from 'design-system/InfoCard';
import { KeyboardAvoidingButton } from 'design-system/KeyboardAvoidingButton';
import useShouldShowUpgrade from 'features/premium/hooks/useShouldShowUpgrade';
import Modal from 'utils/packages/Modal';
import { useLoadBudgetsAndIncomes } from '../hooks/useLoadBudgetsAndIncomes';
import { useSaveBudget } from '../hooks/useSaveBudget';
import { selectBudgets } from '../selectors';
import { isWeb } from '../../../constants';
import { getBudgets } from '../actions';
import { useBudgetRollover } from '../hooks/useBudgetRollover';
import { useBudgetingNavigate } from '../hooks/useBudgetingNavigate';
import { MAX_BUDGET_AMOUNT } from '../utils';
const strings = {
    ok: 'OK',
    totalBudgetErrorTitle: 'Total Budget Error!',
    totalBudgetErrorDescription: 'Your total budget can not be null, set it to start saving money. 🤑',
    editTotalBudgetTitle: 'Edit total budget',
    editTotalBudgetDescription: 'Total budget is how much you want to spend in a given period. This includes both your day-to-day spending and recurring payments.',
    editTotalBudgetResetToolTip: 'Current budget includes both the default amount and rolled-over from the last period. Editing will reset the rollover, making the new total budget your default.',
};
const EditTotalBudgetScreen = withAnimatedNavigationHeader(({ navigation, route, onScrollY }) => {
    const { flow, editedMonthlyBudget, shouldRollover, budgetState: budgetStateFromRoute, shouldResetRollingAmount, period: periodFromRoute, hasPrefilledBudgets, } = route.params;
    const store = useAppStore();
    const thisMonth = useAppSelector((store) => store.feed.feed.cards.thisMonth);
    const { isUnlockedForSpace, onPressUpgrade } = useShouldShowUpgrade({
        benefitId: 'rollingBudgets',
    });
    const hasSetupBudgets = useMemo(() => thisMonth?.budget != null, [thisMonth]);
    const styles = useStyles(styleSet);
    const colors = useColors();
    const isLoggedIn = useAppSelector(selectIsLoggedIn);
    useLayoutEffect(() => {
        if (!isLoggedIn) {
            navigation.setOptions({
                headerRight: () => <HelpButton black/>,
            });
        }
    }, [isLoggedIn]);
    const budgets = useAppSelector(selectBudgets);
    const scrambled = useAppSelector(selectIsScrambled);
    const [budgetState, setBudgetState] = useState([]);
    const [IsFocused, setIsFocused] = React.useState(false);
    const { isRollover } = useBudgetRollover();
    const handleEvent = useCallback((event) => {
        if (isWeb) {
            setIsFocused(event === 'focus');
        }
    }, []);
    const handleFocus = useCallback(() => {
        handleEvent('focus');
    }, [handleEvent]);
    const handleBlur = useCallback(() => {
        handleEvent('blur');
    }, [handleEvent]);
    useEffect(() => {
        // This is when the API returns
        if (budgetStateFromRoute !== undefined) {
            setBudgetState(budgetStateFromRoute);
        }
        else if (budgets && flow !== 'Confirm') {
            setBudgetState(budgets);
        }
    }, [budgetStateFromRoute, budgets, flow, hasPrefilledBudgets]);
    useLoadBudgetsAndIncomes(flow !== 'Confirm');
    const monthly = useMemo(() => budgetState.find((budget) => budget.type === 'overall'), [budgetState]);
    let initialBudgetValue;
    if (flow === 'Set' && !hasSetupBudgets && !hasPrefilledBudgets) {
        initialBudgetValue = '';
    }
    else {
        initialBudgetValue =
            editedMonthlyBudget
                ?.toString()
                .replace(/(\.\d*[1-9])0+$|\.0+$/, '$1') ??
                monthly?.totalLimit
                    ?.toString()
                    .replace(/(\.\d*[1-9])0+$|\.0+$/, '$1') ??
                '';
    }
    const [monthlyBudget, setMonthlyBudget, , monthlyBudgetRef] = useTextInput(Number(initialBudgetValue) <= 0
        ? null
        : Math.min(Number(initialBudgetValue), MAX_BUDGET_AMOUNT));
    const rawMonthlyBudget = useMemo(() => monthlyBudget ?? 0, [monthlyBudget]);
    useAutoFocusInput(monthlyBudgetRef);
    const onChangeValue = useCallback((newValue) => {
        setMonthlyBudget(newValue);
    }, [setMonthlyBudget]);
    const [monthlyBudgetText, onChangeMonthlyBudgetText] = useMoneyInputText(monthlyBudget, onChangeValue);
    const onScroll = useCallback(({ nativeEvent: { contentOffset }, }) => {
        onScrollY(contentOffset.y);
    }, [onScrollY]);
    const dispatch = useAppDispatch();
    const [saving, save] = useSaveBudget();
    const validateTotalBudget = useCallback(() => {
        if (!Number(rawMonthlyBudget)) {
            Alert.alert(strings.totalBudgetErrorTitle, strings.totalBudgetErrorDescription, [
                {
                    onPress: () => {
                        monthlyBudgetRef.current?.focus();
                    },
                    text: strings.ok,
                },
            ]);
            return false;
        }
        return true;
    }, [monthlyBudgetRef, rawMonthlyBudget]);
    const totalSpending = useMemo(() => {
        const sum = budgetState.reduce((prev, budget) => {
            if (budget.type === 'category' && budget.totalLimit) {
                return prev + budget.totalLimit;
            }
            return prev;
        }, 0);
        return Math.round((sum + Number.EPSILON) * 100) / 100;
    }, [budgetState]);
    const hadleAmountLessThanCategories = useCallback((callBack) => () => {
        if (Number(rawMonthlyBudget) < totalSpending) {
            Keyboard.dismiss();
            Modal.showConfirmation({
                title: 'Amount less than categories',
                subtitle: `The total amount you’ve entered is less than your combined category budgets. You can adjust your category budgets from the 'Category budgets' screen.`,
                confirmButtonTitle: 'Cancel',
                cancelButtonTitle: 'Save this amount',
                onConfirm: () => { },
                onPressCancel: () => callBack(),
            });
        }
        else {
            callBack();
        }
    }, [rawMonthlyBudget, totalSpending]);
    const { onSuccess } = useBudgetingNavigate();
    const onPressSave = useRequiredBenefitCallbackWithSpaces(async () => {
        if (!Number(rawMonthlyBudget)) {
            Alert.alert('Hold on 💫', 'You need to set a valid total budget.', [
                {
                    onPress: () => monthlyBudgetRef.current?.focus(),
                    text: 'OK',
                },
            ]);
            return;
        }
        if (hasPrefilledBudgets) {
            navigation.navigateDeprecated('Budgets', {
                period: periodFromRoute,
                monthlyBudget: Number(rawMonthlyBudget),
                budgetState: budgetStateFromRoute,
                flow,
                hasPrefilledBudgets,
            });
            return;
        }
        const result = await saveBudget({
            monthlyBudget: Number(rawMonthlyBudget),
            budgetState,
            shouldRollover: isRollover,
        });
        if (result) {
            dispatch(getBudgets());
            dispatch(getIncomes());
            dispatch(fetchAnalyticsBudgetingData());
            if (flow === 'Confirm') {
                Amplitude.logEvent('BudgetsSetup.Finish');
                if (store.getState().user.isLoggedIn || isWeb) {
                    navigation.navigateDeprecated('Success', {
                        title: 'Success',
                        subTitle: 'Your budget has been set!',
                        onContinue: () => {
                            navigation.popToTop();
                            onSuccess();
                            return true;
                        },
                    });
                }
                else {
                    const route = 'SetBudgetsSuccess';
                    navigation.navigateDeprecated(route);
                }
            }
            else {
                navigation.goBack();
            }
        }
    }, 'budgeting');
    const rateApp = useAppRating();
    const saveBudget = useCallback(async ({ monthlyBudget, budgetState, shouldRollover, }) => {
        if (!validateTotalBudget()) {
            return false;
        }
        const res = await save(monthlyBudget, shouldRollover, budgetState);
        if (res === null)
            return false;
        const [budgetResult, incomeResult] = res;
        if (budgetResult !== null &&
            (periodFromRoute === undefined || incomeResult !== null)) {
            rateApp();
        }
        return true;
    }, [periodFromRoute, rateApp, save, validateTotalBudget]);
    const handleContinuePress = () => {
        if (!Number(rawMonthlyBudget)) {
            Alert.alert('Hold on 💫', 'You need to set a valid total budget.', [
                {
                    onPress: () => monthlyBudgetRef.current?.focus(),
                    text: 'OK',
                },
            ]);
            return;
        }
        const editBudgetState = budgetStateFromRoute?.map((budget) => {
            if (budget.key === 'overall.monthly') {
                return {
                    ...budget,
                    totalLimit: Number(rawMonthlyBudget) || 0,
                };
            }
            return budget;
        });
        navigation.navigateDeprecated('EditBudget', {
            flow: 'Confirm',
            editTotal: true,
            editedMonthlyBudget: Number(rawMonthlyBudget),
            shouldRollover,
            budgetState: editBudgetState,
            shouldResetRollingAmount,
            period: periodFromRoute,
        });
    };
    const { paddingHorizontalStyle } = useAppFrameDimensions(isWeb);
    const defaultCurrency = useAppSelector(selectDefaultCurrency);
    return (<AppView withFrame={false}>
        <AnimatedKeyboardAwareScrollView scrollEventThrottle={8} onScroll={onScroll} enableAutomaticScroll={flow === 'Edit'} keyboardShouldPersistTaps="always" contentContainerStyle={paddingHorizontalStyle} showsVerticalScrollIndicator={false}>
          <StatusBar barStyle="dark-content"/>

          <View style={styles.contentContainer}>
            <View style={styles.headerPadding}>
              <Text TextThin-16 Secondary>
                {strings.editTotalBudgetDescription}
              </Text>
            </View>
            <View style={styles.inputWrapper}>
              <View style={[styles.inputContainer, IsFocused && styles.focused]}>
                <View style={styles.inputLeftContainer}>
                  <Text Text-24 style={styles.inputTextLeft}>
                    {getSymbolFromCurrency(monthly?.currency || defaultCurrency)}
                  </Text>
                  <View style={styles.verticalSeparator}/>
                </View>
                <View style={styles.flex}>
                  <MoneyTextInput placeholder={'0'} onBlur={handleBlur} returnKeyType="done" value={monthlyBudgetText} onFocus={handleFocus} ref={monthlyBudgetRef} onChangeText={onChangeMonthlyBudgetText} maxValue={MAX_BUDGET_AMOUNT} style={styles.textInput} allowFontScaling={false} underlineColorAndroid="transparent"/>
                </View>
              </View>

              {monthly?.rollingAccumulatedLimit ? (<View>
                  <BlurAmountView enabled={scrambled}>
                    <Text TextThin-14 Secondary centered>
                      Default{' '}
                      <Text NumbersThin-14 Secondary centered>
                        {formatAmount(monthly?.baseLimit, monthly?.currency, 2)}
                      </Text>
                    </Text>
                  </BlurAmountView>
                  <BlurAmountView enabled={scrambled}>
                    {!isUnlockedForSpace ? <Spacer h={4}/> : null}
                    <Text TextThin-14 centered color={monthly?.rollingAccumulatedLimit >= 0
                ? colors.status.positive
                : colors.status.negative} {...(!isUnlockedForSpace ? { Gray: true } : {})}>
                      Rolled{' '}
                      {!isUnlockedForSpace ? (<Button title="Upgrade" brandReversed square cards extra small hug onPress={onPressUpgrade}/>) : (<Amount Numbers-14 showSign color={monthly?.rollingAccumulatedLimit >= 0
                    ? colors.status.positive
                    : colors.status.negative} value={{
                    value: monthly.rollingAccumulatedLimit,
                    currency: monthly?.currency,
                }}/>)}
                    </Text>
                  </BlurAmountView>
                </View>) : (<BlurAmountView enabled={scrambled}>
                  <Text TextThin-14 Secondary centered>
                    3-months avg{' '}
                    <Text NumbersThin-14 Secondary centered>
                      {formatAmount(monthly?.previousPeriodAverage || 0, monthly?.currency, 2)}
                    </Text>
                  </Text>
                </BlurAmountView>)}
            </View>
            {monthly?.rollingAccumulatedLimit ? (<>
                <Spacer h={isWeb ? 40 : 8}/>
                <InfoCard>
                  <Text TextThin-14 Secondary style={styles.infoCard}>
                    {strings.editTotalBudgetResetToolTip}
                  </Text>
                </InfoCard>
              </>) : null}
          </View>
        </AnimatedKeyboardAwareScrollView>
        {flow === 'Set' && (<KeyboardAvoidingButton brand title={hasPrefilledBudgets ? 'Continue' : 'Save'} isFetching={saving} onPress={hadleAmountLessThanCategories(onPressSave)}/>)}

        {flow === 'Confirm' && (<KeyboardAvoidingButton brand title="Confirm" isFetching={saving} onPress={hadleAmountLessThanCategories(handleContinuePress)}/>)}
      </AppView>);
}, () => ({
    pageTitle: 'Edit Budget',
    title: 'Edit total budget',
}));
const styleSet = createStyleSheets((colors) => ({
    contentContainer: {
        flex: 1,
        justifyContent: 'space-between',
        paddingBottom: rem(24),
    },
    inputWrapper: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: rem(169),
        marginHorizontal: rem(16),
        marginTop: rem(16),
    },
    inputContainer: {
        backgroundColor: colors.cards.onDark,
        width: rem(270),
        borderRadius: rem(12),
        marginBottom: rem(13),
        justifyContent: 'space-between',
        flexDirection: 'row',
        alignItems: 'center',
        ...(isWeb && {
            borderWidth: 1,
            borderColor: colors.cards.onDark,
            caretColor: colors.text.selection,
        }),
    },
    focused: {
        ...(isWeb && {
            borderWidth: 1,
            borderColor: colors.elements.brand,
        }),
    },
    inputLeftContainer: {
        flexDirection: 'row',
    },
    inputTextLeft: {
        paddingVertical: rem(11.5),
        paddingHorizontal: rem(20),
    },
    verticalSeparator: {
        backgroundColor: colors.background.dark,
        width: 1,
    },
    textInput: {
        ...styles.Numbers_24,
        flex: 1,
        textAlign: 'right',
        color: colors.text.primary,
        paddingHorizontal: rem(16),
    },
    row: {
        flexDirection: 'row',
        alignItems: 'center',
    },
    infoCard: {
        marginLeft: rem(8),
    },
    headerPadding: {
        paddingHorizontal: rem(16),
        paddingBottom: rem(24),
    },
    flex: {
        flex: 1,
    },
}));
export default EditTotalBudgetScreen;
